import React from 'react'

import Button from 'components/Button'
import T from 'components/FormattedMessage'
import SelectFriends from 'components/SelectFriends'
import GameCreationScreen from 'components/GameCreationScreen'

import { FriendsResponse, IUser, GameDetailsResponse } from 'protos'
import { Action, Stream } from 'types'

import helpIconUrl from 'assets/images/users_icon.svg'
import { Snackbar } from '@material-ui/core'

interface Props {
  getGameDetails: () => Stream<GameDetailsResponse>
  getUserFriends: () => Promise<FriendsResponse>
  selectedFriendIds: Set<string>
  onFriendSelect: (friend: IUser) => void
  onFriendDeselect: (friend: IUser) => void
  onFriendsSubmit: () => void
  onBack?: () => void
  errorCode?: string
  clearError: () => void
}

type State = {
  gameDetails: Action<GameDetailsResponse | null>
  friends: Action<FriendsResponse | null>
}

class InviteMoreFriendsScreen extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      friends: { isLoading: true },
      gameDetails: { isLoading: true },
    }
  }

  eventSourceSubscription?: () => void

  async componentDidMount() {
    this.requestUserFriends()
    this.eventSourceSubscription = await this.requestGameDetails()
  }

  componentWillUnmount() {
    this.eventSourceSubscription!()
  }

  requestUserFriends = () => {
    this.props.getUserFriends()
      .then((result: FriendsResponse) => {
        this.setState({ friends: { isLoading: false, result } })
      })
      .catch((err: Error) => {
        this.setState({ friends: { isLoading: false, errorCode: err.message } })
      })
  }

  requestGameDetails = async () => {
    const subscribe = await this.props.getGameDetails()
    return subscribe(
      (result: GameDetailsResponse) => {
        this.setState({ gameDetails: { isLoading: false, result } })
      },
      (err: Error) => {
        this.setState({ gameDetails: { isLoading: false, errorCode: err.message } })
      }
    )
  }

  renderSubmitButton = () => {
    const { selectedFriendIds, onFriendsSubmit } = this.props

    return (
      <Button
        rounded={false}
        fullWidth
        size="large"
        color="primary"
        variant="contained"
        onClick={onFriendsSubmit}
        disabled={selectedFriendIds.size < 1}
      >
        <T id="privategame_action_invite" />
      </Button>
    )
  }

  renderContent = () => {
    const { selectedFriendIds, onFriendSelect, onFriendDeselect, errorCode, clearError } = this.props
    const { gameDetails, friends } = this.state

    if (!gameDetails.result || !friends.result) {
      return null
    }

    const alreadyInvitedFriendIds = gameDetails.result.leaderboard
      .filter(entry => !entry.host && entry.user)
      .map(entry => entry.user!.userId!)

    return (
      <>
        <SelectFriends
          friends={friends.result.friend}
          selectedFriendIds={selectedFriendIds}
          invitedFriendIds={new Set(alreadyInvitedFriendIds)}
          onFriendSelect={onFriendSelect}
          onFriendDeselect={onFriendDeselect}
        />
        <Snackbar
          open={!!errorCode}
          message={errorCode && <T id={errorCode} />}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={clearError}
        />
      </>
    )
  }

  render() {
    const { gameDetails, friends } = this.state
    const { onBack } = this.props

    return (
      <GameCreationScreen
        title={<T id="privategame_title" />}
        isLoading={gameDetails.isLoading || friends.isLoading}
        errorCode={gameDetails.errorCode || friends.errorCode}
        component={this.renderContent}
        onBack={onBack}
        bottomChild={this.renderSubmitButton()}
        help={{
          iconUrl: helpIconUrl,
          title: 'privategame_help_title',
          message: 'privategame_help_text',
          buttonLabel: 'privategame_help_dismiss',
        }}
      />
    )
  }
}

export default InviteMoreFriendsScreen
